import { AlertTriangle, Loader2, X } from "lucide-react";
import { getGenerationExitConfirmCopy } from "../../data/navigation/generationExitConfirm";

export function GenerationExitConfirmDialog({
  open,
  toolLabel,
  variant = "generating",
  onStay,
  onLeave,
}) {
  if (!open) {
    return null;
  }

  const copy = getGenerationExitConfirmCopy(variant);
  const isGenerating = variant === "generating";

  function handleContinue() {
    onLeave?.(isGenerating ? { saveToLibrary: true } : {});
  }

  function handleDoNotSave() {
    onLeave?.(isGenerating ? { saveToLibrary: false } : {});
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="presentation"
    >
      <button
        type="button"
        className="absolute inset-0 bg-[#0f172a]/45 backdrop-blur-[2px]"
        aria-label="Close dialog and stay on page"
        onClick={onStay}
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="generation-exit-title"
        aria-describedby="generation-exit-description"
        className="relative w-full max-w-md rounded-2xl border border-[#e5e7eb] bg-white p-6 shadow-[0_24px_48px_rgba(15,23,42,0.18)]"
      >
        <button
          type="button"
          onClick={onStay}
          className="absolute right-4 top-4 rounded-md p-1 text-[#64748b] hover:bg-[#f8fafc] hover:text-[#111827]"
          aria-label="Close dialog and stay on page"
        >
          <X className="size-4" aria-hidden />
        </button>

        <div className="flex gap-4 pr-6">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary-soft">
            {isGenerating ? (
              <Loader2 className="size-5 animate-spin text-primary" aria-hidden />
            ) : (
              <AlertTriangle className="size-5 text-primary" aria-hidden />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h2
              id="generation-exit-title"
              className="text-lg font-semibold text-[#111827]"
            >
              {copy.title}
            </h2>
            <p
              id="generation-exit-description"
              className="mt-2 text-sm leading-6 text-[#434655]"
            >
              {copy.description}
            </p>
            {isGenerating && toolLabel ? (
              <p className="mt-3 flex items-start gap-2 rounded-lg border border-primary/25 bg-primary-soft px-3 py-2 text-sm text-[#9a3412]">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                <span>
                  <span className="font-semibold">{toolLabel}</span>{" "}
                  {copy.activeToolSuffix}
                </span>
              </p>
            ) : null}
          </div>
        </div>

        <div className="mt-6 flex flex-col-reverse gap-2 border-t border-[#e5e7eb] pt-6 sm:flex-row sm:justify-end">
          {isGenerating ? (
            <>
              <button
                type="button"
                onClick={handleContinue}
                className="rounded-lg border border-primary bg-white px-4 py-2.5 text-sm font-semibold text-[#434655] hover:bg-primary-soft/60"
              >
                {copy.continueLabel}
              </button>
              <button
                type="button"
                onClick={handleDoNotSave}
                className="rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] hover:bg-primary-hover"
              >
                {copy.doNotSaveLabel}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onStay}
                className="rounded-lg border border-primary bg-white px-4 py-2.5 text-sm font-semibold text-[#434655] hover:bg-primary-soft/60"
              >
                {copy.stayLabel}
              </button>
              <button
                type="button"
                onClick={handleContinue}
                className="rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] hover:bg-primary-hover"
              >
                {copy.leaveLabel}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
